import { KeyboardEvent } from 'react';

import Font from '../Font';
import Icon from '../Icon';
import {
  ActiveContainer,
  BaseInputField,
  BaseTextAreaField,
  ErrorContainer,
  HelpTextContainer,
  InactiveContainer,
  InputContainer,
  LeftIconInput,
  RightIconInput,
  SuccessContainer,
  TextAreaContainer
} from './styles';
import { TextFieldProps } from './types';

/*
E.g.: <TextField
        helpText="help text"
        iconLabel="InterfaceInfo"
        id="Bye"
        label="label"
        leftIconInput="InterfaceClose"
        optionalLabel="optional label"
        rightIconInput="InterfaceCheck"
      /> */

const TextField: React.FC<TextFieldProps> = ({
  cursorLeftIcon = 'auto',
  cursorRightIcon = 'auto',
  helpText,
  iconLabel,
  id,
  isHelpTextInline,
  isLabelInline,
  keepHelpTextHeight,
  keepLabelHeight,
  label,
  labelSize,
  leftIconInput,
  onLeftIconClick,
  onRightIconClick,
  optionalLabel,
  rightIconInput,
  state = 'active',
  variant = 'input',
  ...otherProps
}) => {
  const CustomContainer = {
    active: ActiveContainer,
    error: ErrorContainer,
    inactive: InactiveContainer,
    success: SuccessContainer
  }[state];

  const errorText = state === 'error' ? 'error' : 'neutral400';

  const helpTextColor = state === 'success' ? 'success' : errorText;

  return (
    <CustomContainer data-testid="test-textfield">
      {(label || optionalLabel) && (
        <div>
          {label && (
            <div>
              <Font
                aria-label={label}
                customConfiguration={{
                  color: state === 'inactive' ? 'neutral400' : 'textHeading',
                  fontSize: labelSize || undefined,
                  whiteSpace: isLabelInline ? 'nowrap' : 'normal'
                }}
                customTag="label"
                htmlFor={id || 'input'}
                variant="bodyRegular"
              >
                {label}
              </Font>
              {iconLabel && <Icon iconName={iconLabel} />}
            </div>
          )}

          {optionalLabel && (
            <div>
              <Font variant="bodySRegular">{optionalLabel}</Font>
            </div>
          )}
        </div>
      )}
      {keepLabelHeight && !(label || optionalLabel) && (
        <div style={{ visibility: 'hidden' }}>
          <Font variant="bodyRegular">{' - '}</Font>
        </div>
      )}
      {variant !== 'textarea' ? (
        <InputContainer
          $leftIconInput={leftIconInput}
          $rightIconInput={rightIconInput}
        >
          {rightIconInput && (
            <RightIconInput
              cursor={cursorRightIcon}
              iconName={rightIconInput}
              onClick={onRightIconClick}
              onKeyDown={(event: KeyboardEvent) =>
                event.key === 'Enter' &&
                onRightIconClick?.(event as KeyboardEvent<SVGElement>)
              }
              tabIndex={onRightIconClick ? 0 : -1}
            />
          )}
          {leftIconInput && (
            <LeftIconInput
              cursor={cursorLeftIcon}
              iconName={leftIconInput}
              onClick={onLeftIconClick}
              onKeyDown={(event: KeyboardEvent) =>
                event.key === 'Enter' &&
                onLeftIconClick?.(event as KeyboardEvent<SVGElement>)
              }
              tabIndex={onLeftIconClick ? 0 : -1}
            />
          )}
          <BaseInputField
            $state={state}
            id={id}
            {...otherProps}
            aria-label={label}
          />
        </InputContainer>
      ) : (
        <TextAreaContainer>
          <BaseTextAreaField
            $state={state}
            cols={100}
            id={id}
            {...otherProps}
            aria-label={label}
          />
        </TextAreaContainer>
      )}
      {helpText && (
        <HelpTextContainer>
          <Font
            customConfiguration={{
              color: helpTextColor,
              whiteSpace: isHelpTextInline ? 'nowrap' : 'normal'
            }}
            customTag="label"
            variant="bodySRegular"
          >
            {helpText}
          </Font>
        </HelpTextContainer>
      )}
      {keepHelpTextHeight && !helpText && (
        <div style={{ visibility: 'hidden' }}>
          <Font variant="bodyRegular">{' - '}</Font>
        </div>
      )}
    </CustomContainer>
  );
};

export default TextField;
